import { useEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { inspectCopy, type InspectCopyKey } from '@/i18n/ns/inspect';
import panelsCopy from '@/i18n/ns/artifact-panels';
import settingsActionsCopy from '@/i18n/ns/settings-actions';
import type { Change } from '../../../src/apps/selection/patch-values.mjs';
import type { PatchValue } from '../../../src/artifacts/presentation-values.mjs';
import { useInspectHost } from './context';
import { NumberFormatControl, type FormatGroup } from './number-format-control';
import { ColourControls, SpacingControls, type PresentationChange } from './presentation-controls';
import {
  changesAcross,
  colourFromSlots,
  formatChangesAcross,
  formatClearAcross,
  imageFitAcross,
  imageRatioAcross,
  formatFromSlot,
  mixedKeys,
  slotKey,
  spacingFromSlots,
  type DetailSlot,
} from './presentation-slots';
import { colourTokens, spacingTokens, swatchFor } from './presentation-tokens';
import { ImageFitControl, ImageRatioControl } from './image-controls';
import type { ImageFit, ImageRatio } from '../../../src/artifacts/images/values.mjs';
import type { EditKind, InspectSession, InspectSlotView } from './types';

const ORDER = [
  'text',
  'colour',
  'spacing',
  'numberFormat',
  'imageFit',
  'imageRatio',
] as const satisfies readonly EditKind[];
/** The sheet's tabs: every INS presentation property it offers. */
type SheetKind = (typeof ORDER)[number];
const KIND_COPY: Record<SheetKind, InspectCopyKey> = {
  text: 'text',
  colour: 'colour',
  spacing: 'spacing',
  numberFormat: 'numberFormat',
  imageFit: 'imageFit',
  imageRatio: 'imageRatio',
};

/** What every selected target can do directly: the intersection (INSPECTOR-SPEC §8). */
export function sharedEdits(session: InspectSession, targetIds: readonly string[]): SheetKind[] {
  const targets = session.targets.filter(
    (t) => targetIds.includes(t.targetId) && t.status === 'current',
  );
  if (!targets.length || targets.length !== targetIds.length) return [];
  return ORDER.filter((kind) => targets.every((t) => t.editable.includes(kind)));
}

/** The text slot on every selected target, and the value they share (or Mixed). */
export function slotState(
  session: InspectSession,
  targetIds: readonly string[],
  kind: EditKind,
): {
  name: string | null;
  current: string | null;
  mixed: boolean;
  fromData: boolean;
} {
  const slots = session.targets
    .filter((t) => targetIds.includes(t.targetId))
    .map((t) => t.slots?.find((x) => x.kind === kind) ?? null);
  const known = slots.filter((x): x is InspectSlotView => x !== null);
  const fromData = known.some((x) => x.current.status === 'from-data');
  const values = known.map((x) => (x.current.status === 'ready' ? x.current.value : null));
  const same = values.length > 0 && values.every((v) => v !== null && v === values[0]);
  const mixed = known.some((x) => x.current.status === 'mixed') || (values.length > 1 && !same);
  return {
    name:
      known.length === 1 || (known.length && known.every((x) => x.name === known[0].name))
        ? known[0].name
        : null,
    current: same ? values[0] : null,
    mixed,
    fromData,
  };
}

type Step =
  | { kind: 'idle' }
  | { kind: 'preparing' }
  | { kind: 'ready'; operationId: string; before: string; after: string; scopeLabel: string | null }
  | { kind: 'refused'; reasonKey: string }
  | { kind: 'applying' }
  | { kind: 'saved'; versionLabel: string | null }
  | { kind: 'stale' }
  | { kind: 'failed'; reasonKey?: string }
  | { kind: 'undone' | 'undo-failed' | 'undo-conflict' }
  /** A save (or undo) went out but its receipt couldn't be confirmed: it may have committed.
   *  Only the original receipt can settle it; nothing is applied again. */
  | { kind: 'uncertain'; action: 'apply' | 'undo'; operationId: string };

const CLEAR = Symbol('clear-override');

/** ART #3349: a dispatched operation whose commit receipt is not yet readable. */
const UNCONFIRMED = 'receipt_reconciliation_required';
const unconfirmed = (result: { status: string; reasonKey?: string }) =>
  result.status === 'failed' && result.reasonKey === UNCONFIRMED;

/**
 * Text, Colour, Spacing and Number format for the selected targets. Colour, spacing and number
 * format are INS's typed slots (ART's `details`), offered only as declared and prepared with
 * `prepareChanges`; text uses the session's single-slot `prepare`. A value is prepared by INS (no
 * model call), shown as before → after with its shared scope, then applied atomically to the
 * whole set: "Saved to draft", never "ready to publish". An unsupported or refused edit offers
 * the chat instead, explicitly. Escape cancels a pending edit before anything else.
 *
 * INS's Apply makes the selected targets stale before Saved returns. The sheet keeps the
 * operation it started (applying, Saved with Undo, stale or failed) on screen for that same
 * selection; it never pretends the stale targets are still editable.
 */
export function PropertySheet({
  session,
  targetIds,
  onHandoff,
  onMention,
}: {
  session: InspectSession;
  targetIds: string[];
  phone?: boolean;
  onHandoff?: () => void;
  onMention?: () => void;
}) {
  const copy = useNamespaceCopy(inspectCopy);
  const panels = useNamespaceCopy(panelsCopy);
  const settings = useNamespaceCopy(settingsActionsCopy);
  const host = useInspectHost();
  const edits = sharedEdits(session, targetIds);
  const [kind, setKind] = useState<SheetKind | null>(null);
  const [value, setValue] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'idle' });
  const [noChanges, setNoChanges] = useState(false);
  // Pending typed choices: control values keyed by the first target's slot id, and the number
  // format choices in the order they were made. Both start empty for every edit.
  // A pending Reset is recorded as CLEAR: the slot goes back to its default once applied.
  const [picked, setPicked] = useState<Record<string, PatchValue | typeof CLEAR>>({});
  const [formatChoices, setFormatChoices] = useState<(readonly [FormatGroup, string])[]>([]);
  // A pending number-format Reset: the default shows as what it will leave.
  const [formatClearing, setFormatClearing] = useState(false);
  const asked = useRef(0);
  const current = kind && edits.includes(kind) ? kind : (edits[0] ?? null);
  // Results belong to the session, revision and selection they were asked for.
  const selectionKey = `${session.sessionId}|${session.revision}|${targetIds.join('|')}`;
  const slot = slotState(session, targetIds, 'text');
  const textFieldLabel = session.targets
    .filter((target) => targetIds.includes(target.targetId))
    .every((target) => target.slots.filter((entry) => entry.kind === 'text').length === 1)
    ? copy.fieldContent
    : (slot.name ?? copy.text);
  const mixed = slot.mixed;
  /** What the text field starts from: the slot's current value, never an empty box. */
  const baseline = () => slot.current ?? '';
  const selection: DetailSlot[][] = targetIds.map(
    (id) => session.targets.find((t) => t.targetId === id)?.details?.slots ?? [],
  );
  const typed = (k: EditKind | null) =>
    !!k &&
    k !== 'text' &&
    selection.length > 0 &&
    selection.every((slots) =>
      slots.some((x) => x.property === k && x.editable && !!x.presentation),
    );

  const stepRef = useRef<Step>(step);
  stepRef.current = step;
  /** Drop whatever was being prepared: a newer value, a new selection, or an Escape elsewhere. */
  const abandon = () => {
    asked.current += 1;
    const pending = stepRef.current.kind;
    if (pending === 'preparing' || pending === 'ready') session.cancel();
  };
  const resetChoices = () => {
    setValue(baseline());
    setPicked({});
    setFormatChoices([]);
    setFormatClearing(false);
  };

  // A different selection starts a fresh edit; an answer for the old one is ignored. The
  // session's own revision bump after a save is not a different selection for the result.
  // A held result belongs to the session it was made in: only that session's own revision bump
  // keeps it. A new session (even over the same target ids) or a new selection starts fresh.
  const targetKey = `${session.sessionId}|${targetIds.join('|')}`;
  useEffect(() => {
    const kept = stepRef.current.kind;
    if (
      kept === 'applying' ||
      kept === 'saved' ||
      kept === 'stale' ||
      kept === 'failed' ||
      kept === 'undone' ||
      kept === 'undo-failed' ||
      kept === 'undo-conflict' ||
      kept === 'uncertain'
    )
      return;
    abandon();
    resetChoices();
    setStep({ kind: 'idle' });
    setNoChanges(false);
    host?.setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);
  useEffect(() => {
    abandon();
    resetChoices();
    setStep({ kind: 'idle' });
    setNoChanges(false);
    host?.setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // Escape outside the sheet cancelled the edit (the layer told INS): Apply goes away too.
  const editingNow = host?.editing ?? false;
  useEffect(() => {
    if (editingNow) return;
    const pending = stepRef.current.kind;
    if (pending === 'preparing' || pending === 'ready') {
      asked.current += 1;
      setStep({ kind: 'idle' });
      setNoChanges(false);
      resetChoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingNow]);

  // Unmounting mid-edit (the panel closed) cancels the pending operation.
  useEffect(
    () => () => {
      abandon();
      host?.setEditing(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // An operation this sheet started stays visible after INS marks the targets stale.
  const holding =
    step.kind === 'applying' ||
    step.kind === 'saved' ||
    step.kind === 'stale' ||
    step.kind === 'failed' ||
    step.kind === 'undone' ||
    step.kind === 'undo-failed' ||
    step.kind === 'undo-conflict' ||
    step.kind === 'uncertain';
  // A stale target needs selecting again; that is not the same as "can't be changed here".
  const anyStale = session.targets.some(
    (t) => targetIds.includes(t.targetId) && t.status !== 'current',
  );
  const viewOnly =
    !session.canEdit || (step.kind === 'refused' && step.reasonKey === 'not_authorized');
  if ((!edits.length || viewOnly) && !holding) {
    return (
      <div
        className="pc-inspect-props"
        data-testid="inspect-props"
        data-state={viewOnly ? 'view-only' : anyStale ? 'stale' : 'unsupported'}
      >
        <p className="pc-inspect-note">
          {viewOnly ? copy.viewOnly : anyStale ? copy.staleSelect : copy.refused}
        </p>
        {onMention && (
          <div className="pc-inspect-actions">
            <button type="button" className="pc-btn" data-variant="quiet" onClick={onMention}>
              {panels.mentionInChat}
            </button>
          </div>
        )}
        {!viewOnly && !anyStale && onHandoff && (
          <button type="button" className="pc-btn" data-variant="secondary" onClick={onHandoff}>
            {copy.handoff}
          </button>
        )}
      </div>
    );
  }

  /** One prepare at a time; an answer for an older value, choice or selection is dropped. */
  const run = async (ask: () => Promise<Awaited<ReturnType<InspectSession['prepare']>>>) => {
    const ticket = ++asked.current;
    host?.setEditing(true);
    setNoChanges(false);
    setStep({ kind: 'preparing' });
    const result = await ask();
    if (ticket !== asked.current) return;
    setStep(
      result.status === 'ready'
        ? { kind: 'ready', ...result }
        : { kind: 'refused', reasonKey: result.reasonKey },
    );
  };
  const idle = () => {
    abandon();
    host?.setEditing(false);
    setStep({ kind: 'idle' });
    setNoChanges(false);
  };
  /** In flight or unconfirmed: no new prepare may start (only Check again for the original). */
  const locked = () => stepRef.current.kind === 'applying' || stepRef.current.kind === 'uncertain';
  const prepareText = async (next: string) => {
    if (locked()) return;
    setValue(next);
    // Back to what it says now (or emptied): nothing to prepare.
    if (current !== 'text' || next === '' || next === baseline()) return idle();
    await run(() => session.prepare('text', next));
  };
  /** Typed changes for every selected target; a target without the slot refuses the whole. */
  const prepareTyped = async (changes: Change[] | null) => {
    if (changes === null) {
      abandon();
      return setStep({ kind: 'refused', reasonKey: 'uncovered_selection' });
    }
    if (!changes.length) return idle();
    await run(() => session.prepareChanges(changes));
  };
  const pick = (choices: PresentationChange[]) => {
    if (locked()) return;
    const next = { ...picked };
    for (const c of choices) next[c.key] = 'clear' in c ? CLEAR : c.value;
    setPicked(next);
    void prepareTyped(
      changesAcross(
        selection,
        Object.entries(next).map(
          ([key, v]): PresentationChange =>
            v === CLEAR ? { key, clear: true } : { key, value: v as string | number },
        ),
      ),
    );
  };
  const resetFormat = () => {
    if (locked()) return;
    setFormatChoices([]);
    setFormatClearing(true);
    void prepareTyped(formatClearAcross(selection));
  };
  const chooseFormat = (group: FormatGroup, choice: string) => {
    if (locked()) return;
    setFormatClearing(false);
    const next = [...formatChoices.filter(([g]) => g !== group), [group, choice] as const];
    setFormatChoices(next);
    void prepareTyped(formatChangesAcross(selection, next));
  };
  const cancel = () => {
    abandon();
    host?.setEditing(false);
    resetChoices();
    setStep({ kind: 'idle' });
    setNoChanges(false);
  };
  const apply = async () => {
    if (step.kind === 'idle') {
      setNoChanges(true);
      return;
    }
    if (step.kind !== 'ready') return;
    const ticket = asked.current;
    setStep({ kind: 'applying' });
    await settleApply(ticket, step.operationId);
  };
  /** Apply, or read the original receipt again for the same operation (never a second Apply). */
  const settleApply = async (ticket: number, operationId: string, recovery = false) => {
    const result = await session
      .apply(operationId)
      .catch(() => ({ status: 'failed' as const, reasonKey: UNCONFIRMED }));
    // The selection changed while it saved: this result isn't for this sheet.
    if (ticket !== asked.current) return;
    host?.setEditing(false);
    setStep(
      result.status === 'saved'
        ? { kind: 'saved', versionLabel: result.versionLabel ?? null }
        : unconfirmed(result)
          ? { kind: 'uncertain', action: 'apply', operationId }
          : result.reasonKey === 'operation_in_progress'
            ? recovery
              ? { kind: 'uncertain', action: 'apply', operationId }
              : { kind: 'applying' }
            : result.status === 'stale'
              ? { kind: 'stale' }
              : { kind: 'failed', reasonKey: result.reasonKey },
    );
    if (result.status === 'saved') host?.markSaved(targetIds);
  };
  const checkAgain = async () => {
    if (step.kind !== 'uncertain') return;
    const ticket = asked.current;
    const { action, operationId } = step;
    setStep({ kind: 'applying' });
    if (action === 'apply') return settleApply(ticket, operationId, true);
    await settleUndo(ticket, operationId, true);
  };
  const undo = async () => {
    const ticket = ++asked.current;
    setStep({ kind: 'applying' });
    await settleUndo(ticket, 'undo');
  };
  const settleUndo = async (ticket: number, operationId: string, recovery = false) => {
    const result: { status: string; reasonKey?: string } = await session
      .undo()
      .catch(() => ({ status: 'failed', reasonKey: UNCONFIRMED }));
    // Bound like apply: a result for an earlier selection is dropped.
    if (ticket !== asked.current) return;
    setStep(
      result.status === 'saved'
        ? { kind: 'undone' }
        : unconfirmed(result)
          ? { kind: 'uncertain', action: 'undo', operationId }
          : result.reasonKey === 'operation_in_progress'
            ? recovery
              ? { kind: 'uncertain', action: 'undo', operationId }
              : { kind: 'applying' }
            : result.status === 'conflict' ||
                result.reasonKey === 'undo_unavailable' ||
                result.reasonKey === 'undo_conflict'
              ? { kind: 'undo-conflict' }
              : { kind: 'undo-failed' },
    );
    resetChoices();
  };
  // While a save is in flight nothing else can be edited (no overlapping operations).
  // While a save is in flight, or its outcome is unconfirmed, nothing else can be edited: a new
  // prepare would hide the original operation, whose receipt only Check again may read.
  const busy = step.kind === 'applying' || step.kind === 'uncertain';
  /** Reset is offered only when every selected target's matching slot holds a clearable override. */
  const clearableEverywhere = (key: string | null) =>
    !!key &&
    selection.every((slots) => {
      const match = slots.find((x) => slotKey(x) === key);
      return !!match?.clearable && match.presentation?.presence === 'present';
    });
  const first = selection[0] ?? [];
  const mixedSet = mixedKeys(selection);

  // DES1 #3300: a single-value property's Reset (number format, image fit, image ratio) sits on
  // the before → after line, right-aligned (alone on that line while nothing is pending). Never
  // while a save is in flight, unconfirmed or done. An image that isn't available is read-only.
  const fit = current === 'imageFit' ? imageFitAcross(selection) : null;
  const ratio = current === 'imageRatio' ? imageRatioAcross(selection) : null;
  const resetOf =
    current === 'numberFormat' && typed('numberFormat') && clearableEverywhere('numberFormat')
      ? resetFormat
      : fit && fit.available && clearableEverywhere('imageFit')
        ? () => pick([{ key: fit.lead.slotId, clear: true }])
        : ratio && clearableEverywhere('imageRatio')
          ? () => pick([{ key: ratio.lead.slotId, clear: true }])
          : null;
  const formatReset =
    resetOf && (step.kind === 'idle' || step.kind === 'ready' || step.kind === 'refused') ? (
      <button
        type="button"
        className="pc-btn pc-inspect-reset"
        data-variant="quiet"
        disabled={busy}
        onClick={resetOf}
      >
        {copy.reset}
      </button>
    ) : null;

  const field = () => {
    if (!current || !edits.length) return null;
    if (current === 'text') {
      if (slot.fromData)
        return (
          <div className="pc-inspect-field" data-from-data="true">
            <span className="pc-inspect-field-name">{textFieldLabel}</span>
            <textarea className="pc-inspect-input" rows={2} disabled aria-label={textFieldLabel} />
            <p className="pc-inspect-note">{copy.fromData}</p>
          </div>
        );
      return (
        <label className="pc-inspect-field">
          <span className="pc-inspect-field-name">{textFieldLabel}</span>
          <textarea
            className="pc-inspect-input"
            rows={2}
            value={value}
            placeholder={mixed ? copy.mixed : undefined}
            aria-label={textFieldLabel}
            disabled={busy}
            onChange={(event) => void prepareText(event.target.value)}
          />
        </label>
      );
    }
    // Colour, spacing and number format exist only as INS declares them: no declared slot on
    // every selected target, nothing to offer here (the chat can still do it).
    if (!typed(current)) return <p className="pc-inspect-note">{copy.refused}</p>;
    if (current === 'colour') {
      const entries = colourFromSlots(first).map((e) => {
        const lead = first.find((x) => x.slotId === e.key)!;
        const pending = picked[e.key];
        return {
          ...e,
          mixed: mixedSet.has(slotKey(lead) ?? '') && pending === undefined,
          value: pending === CLEAR ? null : typeof pending === 'string' ? pending : e.value,
          clearable: clearableEverywhere(slotKey(lead)) && pending !== CLEAR,
          clearing: pending === CLEAR,
        };
      });
      return (
        <ColourControls
          entries={entries}
          tokensFor={(e) => colourTokens(e.channel, e.declared, copy)}
          swatch={swatchFor}
          phone={host?.phone ?? false}
          disabled={busy}
          onChange={pick}
        />
      );
    }
    if (current === 'spacing') {
      const { groups, allowed } = spacingFromSlots(first);
      const tokens = spacingTokens(allowed, copy);
      const px = (v: PatchValue | typeof CLEAR | undefined) =>
        typeof v === 'number' ? v : (tokens.find((t) => t.key === v)?.px ?? null);
      const shown = groups.map((g) => ({
        ...g,
        entries: g.entries.map((e) => {
          const lead = first.find((x) => x.slotId === e.key)!;
          const cleared = picked[e.key] === CLEAR;
          const pending = px(picked[e.key]);
          return {
            ...e,
            mixed: mixedSet.has(slotKey(lead) ?? '') && pending === null && !cleared,
            value: cleared ? null : (pending ?? e.value),
            clearable: clearableEverywhere(slotKey(lead)) && !cleared,
            clearing: cleared,
          };
        }),
      }));
      return <SpacingControls groups={shown} tokens={tokens} disabled={busy} onChange={pick} />;
    }
    if (current === 'imageFit') {
      if (!fit) return <p className="pc-inspect-note">{copy.refused}</p>;
      const pending = picked[fit.lead.slotId];
      return (
        <ImageFitControl
          value={fit.value}
          defaultFit={fit.defaultFit}
          available={fit.available}
          cropSafe={fit.cropSafe}
          mixed={fit.mixed}
          pending={typeof pending === 'string' ? (pending as ImageFit) : null}
          clearing={pending === CLEAR}
          disabled={busy}
          onChoose={(value) => pick([{ key: fit.lead.slotId, value }])}
        />
      );
    }
    if (current === 'imageRatio') {
      if (!ratio) return <p className="pc-inspect-note">{copy.refused}</p>;
      const pending = picked[ratio.lead.slotId];
      return (
        <ImageRatioControl
          value={ratio.value}
          defaultRatio={ratio.defaultRatio}
          allowed={ratio.allowed}
          mixed={ratio.mixed}
          pending={typeof pending === 'string' ? (pending as ImageRatio) : null}
          clearing={pending === CLEAR}
          disabled={busy}
          onChoose={(value) => pick([{ key: ratio.lead.slotId, value }])}
        />
      );
    }
    const lead = first.find((x) => x.property === 'numberFormat' && x.presentation);
    const state = lead ? formatFromSlot(lead) : null;
    if (!state) return <p className="pc-inspect-note">{copy.refused}</p>;
    const isMixed = mixedSet.has('numberFormat') && !formatChoices.length;
    return (
      <NumberFormatControl
        base={isMixed ? null : state.base}
        seed={state.seed}
        chosen={Object.fromEntries(formatChoices)}
        clearing={formatClearing ? state.effectiveDefault : null}
        isDefault={state.isDefault}
        mixed={isMixed}
        unsupported={state.unsupported}
        valueType={state.valueType}
        disabled={busy}
        onChoose={chooseFormat}
      />
    );
  };

  return (
    <div
      className="pc-inspect-props"
      data-testid="inspect-props"
      data-state={step.kind}
      onKeyDown={(event) => {
        // An Escape an open popover (the colour swatches) already took is not a cancel.
        if (event.key === 'Escape' && host?.editing && !event.defaultPrevented) {
          event.stopPropagation();
          event.nativeEvent.stopImmediatePropagation?.();
          cancel();
        }
      }}
    >
      {!viewOnly && edits.length > 1 && (
        <div className="pc-inspect-kinds" role="tablist" aria-label={copy.details}>
          {edits.map((k) => (
            <button
              key={k}
              type="button"
              role="tab"
              disabled={busy}
              aria-selected={current === k}
              onClick={() => {
                cancel();
                setKind(k);
              }}
            >
              {copy[KIND_COPY[k]]}
            </button>
          ))}
        </div>
      )}
      {!viewOnly && edits.length === 1 && (
        <p className="pc-inspect-note">{copy[KIND_COPY[current!]]}</p>
      )}
      {!viewOnly && field()}
      <div className="pc-inspect-result" role="status" aria-live="polite">
        {step.kind === 'idle' && noChanges && <span>{panels.noChangesToApply}</span>}
        {step.kind === 'preparing' && <span className="pc-inspect-note">{copy.pending}</span>}
        {step.kind !== 'ready' && formatReset && (
          <span className="pc-inspect-change-line">{formatReset}</span>
        )}
        {step.kind === 'ready' && (
          <>
            {formatReset && <span className="pc-inspect-change-line">{formatReset}</span>}
            {step.scopeLabel && <span className="pc-inspect-scope">{step.scopeLabel}</span>}
            <span className="pc-inspect-note" data-testid="inspect-no-model">
              {copy.noModel}
            </span>
          </>
        )}
        {step.kind === 'refused' && <span>{copy.refused}</span>}
        {step.kind === 'applying' && <span>{copy.applying}</span>}
        {step.kind === 'saved' && (
          <>
            <span className="pc-inspect-saved-line">
              <span data-testid="inspect-saved">
                {step.versionLabel
                  ? formatCopy(copy.savedVersion, { version: step.versionLabel })
                  : copy.saved}
              </span>
              <button
                type="button"
                className="pc-btn"
                data-variant="quiet"
                data-size="sm"
                onClick={() => void undo()}
              >
                {copy.undo}
              </button>
            </span>
            <span className="pc-inspect-note">{copy.checkBeforePublish}</span>
          </>
        )}
        {step.kind === 'stale' && (
          <span>
            {session.subjectLabel
              ? formatCopy(copy.staleSubject, { subject: session.subjectLabel })
              : copy.staleApply}
          </span>
        )}
        {step.kind === 'failed' && (
          <span>{step.reasonKey === 'not_authorized' ? copy.viewOnly : copy.failed}</span>
        )}
        {step.kind === 'undone' && <span>{settings.undone}</span>}
        {step.kind === 'undo-failed' && <span>{copy.undoFailed}</span>}
        {step.kind === 'undo-conflict' && <span>{copy.undoConflict}</span>}
        {step.kind === 'uncertain' && (
          <span className="pc-inspect-saved-line" data-testid="inspect-uncertain">
            <span>{copy.saveUncertain}</span>
            <button
              type="button"
              className="pc-btn"
              data-variant="quiet"
              data-size="sm"
              onClick={() => void checkAgain()}
            >
              {copy.checkAgain}
            </button>
          </span>
        )}
      </div>
      <div className="pc-inspect-actions">
        {(step.kind === 'idle' ||
          step.kind === 'ready' ||
          step.kind === 'preparing' ||
          step.kind === 'applying') && (
          <>
            <button
              type="button"
              className="pc-btn"
              data-variant="primary"
              disabled={step.kind === 'applying'}
              data-testid="inspect-apply"
              onClick={() => void apply()}
            >
              {step.kind === 'applying' ? copy.applying : copy.apply}
            </button>
            <button
              type="button"
              className="pc-link"
              disabled={step.kind === 'applying'}
              onClick={cancel}
            >
              {copy.cancel}
            </button>
          </>
        )}
        {step.kind === 'refused' && onHandoff && (
          <button type="button" className="pc-btn" data-variant="secondary" onClick={onHandoff}>
            {copy.handoff}
          </button>
        )}
        {onMention && (
          <button type="button" className="pc-btn" data-variant="quiet" onClick={onMention}>
            {panels.mentionInChat}
          </button>
        )}
      </div>
    </div>
  );
}
